import { entityPaths, type Entity, type Vec2 } from '@lakaran/geometry';
import type { CadEditor } from '../src/editor/editor';

/**
 * "This card is that shape": the drawing side of the node ↔ geometry link.
 *
 * Hovering or picking a card in the node editor paints the geometry that card is responsible for as
 * a glow over the viewport. It is an overlay only — nothing is selected, nothing is written to the
 * document — and it follows pan and zoom like the trim and mirror overlays do.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface LinkGlow {
  /** Light up these entities; call with `null` (or an empty list) to clear. */
  show(entities: readonly Entity[] | null): void;
  dispose(): void;
}

export function createLinkGlow(editor: CadEditor, host: HTMLElement): LinkGlow {
  const layer = document.createElement('div');
  layer.className = 'node-link-layer';
  layer.hidden = true;
  const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
  svg.classList.add('node-link-svg');
  layer.append(svg);
  host.append(layer);

  let current: Entity[] = [];
  let unlisten: (() => void) | null = null;

  const draw = (): void => {
    if (current.length === 0) return;
    svg.setAttribute('width', String(editor.viewport.width));
    svg.setAttribute('height', String(editor.viewport.height));
    svg.replaceChildren();
    for (const entity of current) {
      for (const path of entityPaths(entity)) {
        if (path.points.length < 2) continue;
        const shape = document.createElementNS(SVG_NS, 'polyline');
        shape.setAttribute('points', screenPoints(path.points));
        shape.setAttribute('class', 'node-link-path');
        if (path.closed) shape.setAttribute('data-closed', '');
        svg.append(shape);
      }
    }
  };

  const screenPoints = (points: readonly Vec2[]): string =>
    points
      .map((point) => {
        const screen = editor.viewport.worldToScreen(point);
        return `${screen.x.toFixed(1)},${screen.y.toFixed(1)}`;
      })
      .join(' ');

  const show = (entities: readonly Entity[] | null): void => {
    if (!entities || entities.length === 0) {
      current = [];
      layer.hidden = true;
      svg.replaceChildren();
      unlisten?.();
      unlisten = null;
      return;
    }
    current = [...entities];
    layer.hidden = false;
    unlisten ??= editor.events.on('view', draw);
    draw();
  };

  return {
    show,
    dispose() {
      unlisten?.();
      unlisten = null;
      layer.remove();
    },
  };
}
